/**
 * Buffers an SDP answer until a relay candidate arrives, gathering
 * completes, or a short timeout fires — whichever comes first.
 *
 * With pre-warming (iceCandidatePoolSize), relay candidates typically
 * arrive within ~50-200ms. The timeout is a safety net so signaling
 * is never blocked for long; trickle ICE still delivers remaining
 * candidates after the SDP is forwarded.
 */
export class SdpGatherBuffer {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private settled = false;
  private detachListeners: (() => void) | null = null;

  constructor(private readonly timeoutMs: number = 500) {}

  /**
   * Wait for relay candidate or gathering completion, then resolve
   * with the final SDP (which may include baked-in candidates).
   */
  buffer(
    rtcPc: RTCPeerConnection,
    initialSdp: RTCSessionDescriptionInit,
  ): Promise<RTCSessionDescriptionInit> {
    this.cancel();
    this.settled = false;

    if (rtcPc.iceGatheringState === 'complete') {
      return Promise.resolve(rtcPc.localDescription ?? initialSdp);
    }

    return new Promise<RTCSessionDescriptionInit>((resolve) => {
      const detach = () => {
        this.clearTimer();
        rtcPc.removeEventListener('icecandidate', onIceCandidate);
        rtcPc.removeEventListener('icegatheringstatechange', onGatheringComplete);
        this.detachListeners = null;
      };
      this.detachListeners = detach;

      const settle = () => {
        if (this.settled) return;
        this.settled = true;
        detach();
        resolve(rtcPc.localDescription ?? initialSdp);
      };

      const onIceCandidate = (event: Event) => {
        const rtcEvent = event as RTCPeerConnectionIceEvent;
        if (rtcEvent.candidate?.type === 'relay') settle();
      };

      const onGatheringComplete = () => {
        if (rtcPc.iceGatheringState === 'complete') settle();
      };

      rtcPc.addEventListener('icecandidate', onIceCandidate);
      rtcPc.addEventListener('icegatheringstatechange', onGatheringComplete);
      this.timer = setTimeout(settle, this.timeoutMs);
    });
  }

  cancel(): void {
    this.settled = true;
    this.detachListeners?.();
    this.clearTimer();
  }

  private clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
